import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash01, Edit05 } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import JobForm from "./JobForm";
import { getJob, updateJob, deleteJob, type JobWithRelations, type UpdateJobPayload } from "@/api/jobs";

const CAN_EDIT = ["admin", "owner", "manager", "mechanic"];
const CAN_DELETE = ["admin", "owner"];

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  awaiting_parts: "bg-yellow-100 text-yellow-700",
  completed: "bg-green-100 text-green-700",
  invoiced: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-700",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  awaiting_parts: "Awaiting Parts",
  completed: "Completed",
  invoiced: "Invoiced",
  cancelled: "Cancelled",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState<JobWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEdit = CAN_EDIT.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchJob = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setJob(await getJob(id));
    } catch {
      navigate("/jobs", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleEdit = async (payload: UpdateJobPayload) => {
    if (!id) return;
    setSaving(true);
    try {
      await updateJob(id, payload);
      setEditOpen(false);
      fetchJob();
      toast.success("Job updated");
    } catch {
      toast.error("Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await deleteJob(id);
      toast.success("Job deleted");
      navigate("/jobs", { replace: true });
    } catch {
      toast.error("Failed to delete job");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !job) {
    return (
      <PageLayout title="Loading...">
        <div className="py-12 text-center text-text-tertiary">Loading job...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Job #${job.jobNumber}`}
      subtitle={job.description ?? undefined}
      actions={
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button size="md" color="secondary" iconLeading={Edit05} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button size="md" color="primary-destructive" iconLeading={Trash01} onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-6 print:hidden">
        <Button size="sm" color="link-gray" iconLeading={ArrowLeft} onClick={() => navigate("/jobs")}>
          Back to Jobs
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Job Details */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-md font-semibold text-text-primary">Job Details</h2>
            <StatusBadge status={job.status} />
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Job No.</span>
              <span className="text-sm text-text-primary font-medium">#{job.jobNumber}</span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Created</span>
              <span className="text-sm text-text-primary">{new Date(job.createdAt).toLocaleDateString()}</span>
            </div>
            {job.estimatedCompletion && (
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Est. Completion</span>
                <span className="text-sm text-text-primary">{job.estimatedCompletion}</span>
              </div>
            )}
            {job.actualCompletion && (
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Completed</span>
                <span className="text-sm text-text-primary">{job.actualCompletion}</span>
              </div>
            )}
            {job.assignee && (
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Assigned To</span>
                <span className="text-sm text-text-primary">{job.assignee.firstName} {job.assignee.lastName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Customer & Vehicle */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6">
          <h2 className="text-md font-semibold text-text-primary mb-4">Customer & Vehicle</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Customer</span>
              <span className="text-sm">
                {job.customer ? (
                  <Link to={`/customers/${job.customer.id}`} className="text-fg-brand-primary hover:underline">
                    {job.customer.firstName} {job.customer.lastName}
                  </Link>
                ) : "—"}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm text-text-tertiary w-28 shrink-0">Phone</span>
              <span className="text-sm text-text-secondary">{job.customer?.phone ?? "—"}</span>
            </div>
            {job.vehicle && (
              <>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-text-tertiary w-28 shrink-0">Vehicle</span>
                  <Link to={`/vehicles/${job.vehicle.id}`} className="text-sm text-fg-brand-primary hover:underline">
                    {job.vehicle.make} {job.vehicle.model} ({job.vehicle.year})
                  </Link>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-text-tertiary w-28 shrink-0">License Plate</span>
                  <span className="text-sm text-text-primary font-medium">{job.vehicle.licensePlate}</span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-sm text-text-tertiary w-28 shrink-0">Mileage</span>
                  <span className="text-sm text-text-secondary">{job.vehicle.mileage != null ? `${job.vehicle.mileage.toLocaleString()} km` : "—"}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description & Diagnosis */}
      {(job.description || job.diagnosis) && (
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <h2 className="text-md font-semibold text-text-primary mb-4">Work Details</h2>
          <div className="space-y-4">
            {job.description && (
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Description</p>
                <p className="text-sm text-text-secondary">{job.description}</p>
              </div>
            )}
            {job.diagnosis && (
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Diagnosis</p>
                <p className="text-sm text-text-secondary">{job.diagnosis}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {job.notes && (
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <h2 className="text-md font-semibold text-text-primary mb-2">Notes</h2>
          <p className="text-sm text-text-secondary">{job.notes}</p>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogTitle>Edit Job</DialogTitle>
        <DialogDescription>Update the job details.</DialogDescription>
        <JobForm
          key={job.id}
          defaultValues={job}
          onSubmit={handleEdit}
          onCancel={() => setEditOpen(false)}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogTitle>Delete Job</DialogTitle>
        <DialogDescription>Are you sure you want to delete job #{job.jobNumber}? This cannot be undone.</DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button size="md" color="primary-destructive" isLoading={saving} isDisabled={saving} onClick={handleDelete}>Delete</Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
